import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import ServiceCard from "@/components/ServiceCard";
import { useAuth } from "@/contexts/auth";
import { useFavorites } from "@/contexts/FavoritesProvider";

import { fetchServiceProvidersByIds } from "@/services/providers/providerCatalogService";
import { Provider, ServiceProviderRow } from "@/types/supabase";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { FavoritesHeader } from "@/components/favorites/FavoritesHeader";
import { FavoritesEmpty } from "@/components/favorites/FavoritesEmpty";
import { debugLogger } from '@/utils/debug';
import { useTranslation } from "react-i18next";
import PageHero from "@/components/layout/PageHero";
import {
  Loader2
} from "lucide-react";

const ITEMS_PER_PAGE = 6;

type FavoriteProviderRow = ServiceProviderRow & {
  created_at?: string;
  updated_at?: string;
  is_top_rated?: boolean;
};

const FavoritesPage = () => {
  const { user, loading } = useAuth();
  const { favorites, isLoading } = useFavorites();
  const { t } = useTranslation('common');
  const [favoriteProviders, setFavoriteProviders] = useState<Provider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch provider data whenever favorites change
  useEffect(() => {
    const fetchProviderData = async () => {
      if (favorites.length === 0) {
        setFavoriteProviders([]);
        setFilteredProviders([]);
        return;
      }

      debugLogger.log("Fetching data for favorite provider IDs:", favorites);
      setIsLoadingProviders(true);

      try {
        // Filter out non-UUID formatted provider IDs to avoid SQL errors
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        const validUuids = favorites.filter(id => uuidPattern.test(id));
        
        if (validUuids.length === 0) {
          debugLogger.log("No valid UUIDs found in favorites");
          setFavoriteProviders([]);
          setFilteredProviders([]);
          setIsLoadingProviders(false);
          return;
        }

        // Fetch real provider data from Supabase only for valid UUIDs
        const data = await fetchServiceProvidersByIds(validUuids, 'approved');

        debugLogger.log("Fetched provider data:", data);

        const typedProviders = (data as FavoriteProviderRow[]).map((item) => ({
          id: item.id,
          name: item.name,
          image_url: item.image_url,
          description: item.description,
          category: item.category,
          categories: item.categories || [],
          whatsapp_number: item.whatsapp_number,
          suburbs: item.suburbs || [],
          is_verified: item.is_verified || false,
          is_featured: item.is_featured || false,
          is_top_rated: item.is_top_rated || false,
          rating: 0,
          review_count: 0,
          status: item.status,
          user_id: item.user_id,
          created_at: item.created_at,
          updated_at: item.updated_at,
          website_url: item.website_url,
          areas_served: [], // Empty array since field was removed
          category_id: null // Set to null since field was removed
        })) as Provider[];

        setFavoriteProviders(typedProviders);
        setFilteredProviders(typedProviders);
      } catch (error) {
        console.error('Error fetching favorite providers:', error);
      } finally {
        setIsLoadingProviders(false);
      }
    };

    if (user && !isLoading) {
      fetchProviderData();
    }
  }, [favorites, user, isLoading]);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProviders(favoriteProviders);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = favoriteProviders.filter(provider => 
        provider.name.toLowerCase().includes(query) || 
        provider.description.toLowerCase().includes(query) ||
        provider.categories.some(cat => cat.toLowerCase().includes(query)) ||
        (provider.suburbs && provider.suburbs.some(suburb => suburb.toLowerCase().includes(query)))
      );
      setFilteredProviders(filtered);
    }
    // Reset to first page when search query changes
    setCurrentPage(1);
  }, [searchQuery, favoriteProviders]);

  // Redirect to login if not authenticated
  if (!loading && !user) {
    return <Navigate to="/auth?redirect=/favorites" replace />;
  }

  const isLoaderShown = loading || isLoading || isLoadingProviders;

  // Pagination calculations
  const totalPages = Math.ceil(filteredProviders.length / ITEMS_PER_PAGE);
  const paginatedProviders = filteredProviders.slice(
    (currentPage - 1) * ITEMS_PER_PAGE, 
    currentPage * ITEMS_PER_PAGE
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <Pagination className="mt-8">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              aria-disabled={currentPage === 1}
              className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>
          
          {[...Array(totalPages)].map((_, index) => {
            const pageNumber = index + 1;
            // Show first page, current page, and last page
            if (pageNumber === 1 || pageNumber === totalPages || Math.abs(pageNumber - currentPage) <= 1) {
              return (
                <PaginationItem key={pageNumber}>
                  <PaginationLink 
                    isActive={pageNumber === currentPage}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </PaginationLink>
                </PaginationItem>
              );
            } else if (Math.abs(pageNumber - currentPage) === 2) {
              // Show ellipsis for gaps
              return (
                <PaginationItem key={`ellipsis-${pageNumber}`}>
                  <div className="flex h-9 w-9 items-center justify-center">...</div>
                </PaginationItem>
              );
            }
            return null;
          })}
          
          <PaginationItem>
            <PaginationNext 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              aria-disabled={currentPage === totalPages}
              className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    );
  };

  return (
    <Layout>
      <div className="bg-brand-mist">
        <PageHero
          title={t("favorites.title")}
          description={t("favorites.description")}
          contentClassName="py-12 md:py-16"
        />

        <div className="container mx-auto px-4 py-8 md:py-10">

          {isLoaderShown ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-whatsapp" />
              <span className="ml-2 text-brand-slate">{t("favorites.loading")}</span>
            </div>
          ) : favorites.length === 0 ? (
            <FavoritesEmpty type="no-favorites" />
          ) : filteredProviders.length === 0 ? (
            <FavoritesEmpty 
              type="no-search-results" 
              onClearSearch={() => setSearchQuery("")}
            />
          ) : (
            <>
              <FavoritesHeader 
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filteredCount={filteredProviders.length}
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedProviders.map(provider => (
                  <ServiceCard 
                    key={provider.id} 
                    id={provider.id} 
                    name={provider.name} 
                    image={provider.image_url} 
                    categories={provider.categories} 
                    suburbs={provider.suburbs} 
                    description={provider.description} 
                    rating={provider.rating} 
                    user_id={provider.user_id}
                    isVerified={provider.is_verified}
                    isFeatured={provider.is_featured}
                    websiteUrl={provider.website_url || undefined}
                    pageType="favorites"
                    userProfileVerified={provider.user_profile_verified}
                    whatsapp_number={provider.whatsapp_number}
                  />
                ))}
              </div>
              
              {renderPagination()}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default FavoritesPage;
